<template>
  <div class="metric-input" :class="{ 'metric-input--error': hasError }">
    <label :for="inputId" class="metric-input__label">
      {{ label }}
      <span v-if="required" class="metric-input__required">*</span>
    </label>
    <div class="metric-input__wrapper">
      <span v-if="prefix" class="metric-input__prefix">{{ prefix }}</span>
      <input
        :id="inputId"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :min="min"
        :max="max"
        :step="step"
        class="metric-input__field"
        :class="{ 'metric-input__field--has-prefix': prefix }"
        @input="handleInput"
        @blur="handleBlur"
      />
      <span v-if="suffix" class="metric-input__suffix">{{ suffix }}</span>
    </div>
    <p v-if="hint && !hasError" class="metric-input__hint">{{ hint }}</p>
    <p v-if="hasError" class="metric-input__error">{{ errorMessage }}</p>
  </div>
</template>

<script>
/**
 * MetricInput - Form input for entering metrics
 * @description Styled input with label, prefix/suffix, and validation
 * @prop {String} label - Input label
 * @prop {String|Number} value - Input value (v-model compatible)
 * @prop {String} type - Input type (text, number, etc.)
 * @prop {String} placeholder - Placeholder text
 * @prop {String} prefix - Prefix text (e.g., "EUR")
 * @prop {String} suffix - Suffix text (e.g., "%")
 * @prop {String} hint - Help text below input
 * @prop {String} errorMessage - Error message to display
 * @prop {Boolean} required - Is field required
 * @prop {Boolean} disabled - Is field disabled
 * @prop {Number} min - Minimum value for number inputs
 * @prop {Number} max - Maximum value for number inputs
 * @prop {Number} step - Step value for number inputs
 */
export default {
  name: "MetricInput",
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    prefix: {
      type: String,
      default: "",
    },
    suffix: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    step: {
      type: Number,
      default: null,
    },
  },
  computed: {
    /**
     * Generates unique ID for input
     * @returns {String}
     */
    inputId() {
      return `metric-input-${this._uid}`;
    },
    /**
     * Checks if there's an error to display
     * @returns {Boolean}
     */
    hasError() {
      return !!this.errorMessage;
    },
  },
  methods: {
    /**
     * Handles input events and emits value
     * @param {Event} event - Input event
     */
    handleInput(event) {
      const val =
        this.type === "number"
          ? parseFloat(event.target.value) || 0
          : event.target.value;
      this.$emit("input", val);
    },
    /**
     * Handles blur events for validation
     * @param {Event} event - Blur event
     */
    handleBlur(event) {
      this.$emit("blur", event);
    },
  },
};
</script>

<style scoped>
.metric-input {
  margin-bottom: 20px;
}

.metric-input__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--k-text-secondary);
  margin-bottom: 8px;
}

.metric-input__required {
  color: var(--k-danger);
  margin-left: 2px;
}

.metric-input__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.metric-input__prefix,
.metric-input__suffix {
  position: absolute;
  font-size: 14px;
  font-weight: 500;
  color: var(--k-text-tertiary);
  pointer-events: none;
}

.metric-input__prefix {
  left: 14px;
}

.metric-input__suffix {
  right: 14px;
}

.metric-input__field {
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  font-family: inherit;
  font-weight: 500;
  color: var(--k-text-primary);
  background: var(--k-bg-primary);
  border: 1px solid var(--k-border-color-strong);
  border-radius: var(--k-radius-md);
  outline: none;
  transition: all var(--k-transition-fast);
}

.metric-input__field--has-prefix {
  padding-left: 48px;
}

.metric-input__field:focus {
  border-color: var(--k-accent);
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15);
}

.metric-input__field:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--k-bg-tertiary);
}

.metric-input__field::placeholder {
  color: var(--k-text-tertiary);
  font-weight: 400;
}

/* Remove number input spinners */
.metric-input__field[type="number"]::-webkit-inner-spin-button,
.metric-input__field[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.metric-input__field[type="number"] {
  -moz-appearance: textfield;
}

.metric-input__hint,
.metric-input__error {
  font-size: 12px;
  margin-top: 6px;
  margin-bottom: 0;
}

.metric-input__hint {
  color: var(--k-text-tertiary);
}

.metric-input__error {
  color: var(--k-danger);
}

/* Error State */
.metric-input--error .metric-input__field {
  border-color: var(--k-danger);
}

.metric-input--error .metric-input__field:focus {
  box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.15);
}
</style>

